import React, { useState } from 'react';
import styled from 'styled-components';
import AppErrorScreen from '~/components/template/AppErrorScreen';
import BndlLoading from '~/components/atom/BndlLoading';
import { Body, Heading4, Variant } from '~/components/atom/Typography';
import { isInitialLoadStatus } from '~/graphql/ApolloConstants';
import { useGetBillingDetailsQuery } from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import TextButton from '~/components/atom/TextButton';
import JustificationContainer from '~/components/atom/JustificationContainer';
import TEST_ID from './index.testid';
import Divider from '~/components/atom/Divider';
import UpdatePaymentMethod from './components/UpdatePaymentMethod';

export type Props = {};

const text = {
  noBillingSetup: 'Je hebt nog geen betaalmethode',
  paymentsTitle: 'Betaalmethode',
  changePaymentDetails: 'Wijzig betaalmethode',
  paymentDescription: 'Betaling vindt plaats via ',
  paymentTypeDescription: 'SEPA automatische incasso.',
};

const PaymentDetails: React.FCC<Props> = ({ dataTestId, ...rest }) => {
  const account = useCurrentAccount();

  const [showModal, setShowModal] = useState(false);
  const hideModal = () => setShowModal(false);
  const { data, networkStatus, error } = useGetBillingDetailsQuery({
    variables: {
      accountId: account.id,
    },
  });

  if (isInitialLoadStatus(networkStatus)) {
    // If there are no overages we do not want to show anything, so also don't show loading
    return <BndlLoading />;
  }

  if (error) {
    return <AppErrorScreen inline setBackgroundColor={false} />;
  }

  if (!data?.getBillingDetails) {
    return null;
  }

  return (
    <>
      {showModal && (
        <UpdatePaymentMethod
          onClose={hideModal}
          onComplete={() => hideModal()}
        />
      )}
      <Container data-testid={dataTestId} {...rest}>
        <Heading4 variant={Variant.primary}>{text.paymentsTitle}</Heading4>
        {data.getBillingDetails.last4 && (
          <>
            <Body margin={[null]} size="base">
              {text.paymentDescription}
              {text.paymentTypeDescription}
            </Body>
            <Body
              data-testid={TEST_ID.BANK_ACCOUNT_NUMBER}
              margin={[null]}
              size="base"
            >
              {`Van rekening **** **${data.getBillingDetails.last4.substring(
                0,
                2,
              )} ${data.getBillingDetails.last4.substring(2, 4)}`}
            </Body>
          </>
        )}
        <JustificationContainer padding={['s', null]}>
          <TextButton
            padding={[null]}
            label={text.changePaymentDetails}
            onClick={() => setShowModal(true)}
            dataTestId={TEST_ID.UPDATE_TRIGGER}
          />
        </JustificationContainer>
      </Container>
      <Divider margin={['xxs', null, 'xs', null]} />
    </>
  );
};

const Container = styled.div<{}>``;

export default PaymentDetails;
